class WLSNSearchForm extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      loading: 'loading',
      active: 'active',
      resetButton: 'js-wlsn-search-show-reset-button',
      inputSearch: 'wlsn-search-form input[type="search"]',
    };

    this.bindMethods = {
      handleInput: this._handleInput.bind(this),
      handleReset: this._handleReset.bind(this),
      handleResetButtonClick: this._handleResetButtonClick.bind(this),
    };
  }

  /**
   * Get DOM elements
   * @returns {object} - Object containing DOM element references
   */
  get elements() {
    return {
      form: this.querySelector('form'),
      input: this.querySelector('input[type="search"]'),
      resetButton: this.querySelector('button[type="reset"]'),
    };
  }

  /**
   * Connected callback lifecycle method
   * @returns {void}
   */
  connectedCallback() {
    const { form, input } = this.elements;

    if (!input || !form) return;

    this._attachEventListeners();
    this._initializeState();
  }

  /**
   * Disconnected callback lifecycle method
   * @returns {void}
   */
  disconnectedCallback() {
    this._removeEventListeners();
  }

  /**
   * Initialize state
   * @returns {void}
   */
  _initializeState() {
    const { input } = this.elements;

    if (!input) return;

    const value = input.value;

    // Handle active class for forms with js-wlsn-search-show-reset-button
    document.querySelectorAll(`.${this.classes.resetButton}`).forEach((f) => {
      if (value) {
        f.classList.add(this.classes.active);
      } else {
        f.classList.remove(this.classes.active);
      }
    });
  }

  /**
   * Attach event listeners
   * @returns {void}
   */
  _attachEventListeners() {
    const { input, form, resetButton } = this.elements;

    if (!input || !form || !resetButton) return;

    input.addEventListener('input', this.bindMethods.handleInput);
    form.addEventListener('reset', this.bindMethods.handleReset);

    if (resetButton) {
      resetButton.addEventListener('click', this.bindMethods.handleResetButtonClick);
    }
  }

  /**
   * Remove event listeners
   * @returns {void}
   */
  _removeEventListeners() {
    const { input, form, resetButton } = this.elements;

    if (input) {
      input.removeEventListener('input', this.bindMethods.handleInput);
    }
    if (form) {
      form.removeEventListener('reset', this.bindMethods.handleReset);
    }
    if (resetButton) {
      resetButton.removeEventListener('click', this.bindMethods.handleResetButtonClick);
    }
  }

  /**
   * Handle input
   * @param {Event} e - Input event
   * @returns {void}
   */
  _handleInput(e) {
    const value = e.target.value;

    // Handle active class for forms with js-wlsn-search-show-reset-button
    document.querySelectorAll(`.${this.classes.resetButton}`).forEach((form) => {
      if (value) {
        form.classList.add(this.classes.active);
      } else {
        form.classList.remove(this.classes.active);
      }
    });

    // Sync input values across all search forms
    document.querySelectorAll(`${this.classes.inputSearch}`).forEach((input) => {
      if (input !== e.target) {
        input.value = value;
      }
    });
  }

  /**
   * Handle reset
   * @returns {void}
   */
  _handleReset() {
    const { input } = this.elements;

    if (!input) return;

    input.value = '';
    input.focus();

    // Remove active class from forms with js-wlsn-search-show-reset-button
    document.querySelectorAll(`.${this.classes.resetButton}`).forEach((f) => {
      f.classList.remove(this.classes.active);
    });

    // Clear other search inputs
    document.querySelectorAll(`${this.classes.inputSearch}`).forEach((i) => {
      if (input !== i) {
        i.value = '';
      }
    });
  }

  /**
   * Handle reset button click
   * @param {Event} e - Reset button click event
   * @returns {void}
   */
  _handleResetButtonClick(e) {
    e.preventDefault();
    const { input } = this.elements;

    if (!input) return;

    input.value = '';
    input.focus();

    // Remove active class from forms with js-wlsn-search-show-reset-button
    document.querySelectorAll(`.${this.classes.resetButton}`).forEach((f) => {
      f.classList.remove(this.classes.active);
    });

    // Clear other search inputs
    document.querySelectorAll(`${this.classes.inputSearch}`).forEach((i) => {
      if (input !== i) {
        i.value = '';
      }
    });
  }

  /**
   * Set loading state
   * @returns {void}
   */
  setLoading() {
    this.setAttribute('disabled', true);
    this.classList.add(this.classes.loading);
  }

  /**
   * Remove loading state
   * @returns {void}
   */
  removeLoading() {
    this.removeAttribute('disabled');
    this.classList.remove(this.classes.loading);
  }
}

if (!customElements.get('wlsn-search-form')) {
  customElements.define('wlsn-search-form', WLSNSearchForm);
}
